// VideoTutorial.tsx - Modal component for displaying video tutorials
import React, { useEffect, useRef } from 'react';
import { Modal, ModalDialog, ModalClose, Typography, Box, IconButton, Chip } from '@mui/joy';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import CloseIcon from '@mui/icons-material/Close';

export interface VideoTutorialConfig {
  id: string;
  title: string;
  description: string;
  videoUrl: string; // YouTube embed URL or direct video URL
  duration: string; // e.g., "90 seconds"
  thumbnail?: string; // Optional thumbnail URL
}

interface VideoTutorialProps {
  open: boolean;
  onClose: () => void;
  video: VideoTutorialConfig | null;
}

export const VideoTutorial: React.FC<VideoTutorialProps> = ({ open, onClose, video }) => {
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    // Pause video when modal closes
    if (!open && videoRef.current) {
      videoRef.current.pause();
    }
  }, [open]);

  if (!video) return null;

  const isYouTube = video.videoUrl.includes('youtube.com') || video.videoUrl.includes('youtu.be');
  const isVimeo = video.videoUrl.includes('vimeo.com');

  // Convert YouTube URLs to embed format
  const getEmbedUrl = (url: string) => {
    if (url.includes('youtube.com/watch?v=')) {
      const videoId = url.split('v=')[1]?.split('&')[0];
      return `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`;
    }
    if (url.includes('youtu.be/')) {
      const videoId = url.split('youtu.be/')[1]?.split('?')[0];
      return `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`;
    }
    if (url.includes('vimeo.com/')) {
      const videoId = url.split('vimeo.com/')[1]?.split('?')[0];
      return `https://player.vimeo.com/video/${videoId}?autoplay=1`;
    }
    return url;
  };

  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog
        sx={{
          maxWidth: '900px',
          width: '90vw',
          p: 0,
          borderRadius: 'md',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            p: 2,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <PlayCircleIcon sx={{ fontSize: 28 }} />
            <Box>
              <Typography level="h4" sx={{ color: 'white', fontWeight: 600 }}>
                {video.title}
              </Typography>
              <Typography level="body-sm" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                {video.description}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip size="sm" variant="soft" sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}>
              {video.duration}
            </Chip>
            <IconButton
              onClick={onClose}
              sx={{
                color: 'white',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>

        {/* Video Player */}
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            paddingTop: '56.25%', // 16:9 aspect ratio
            bgcolor: 'black',
          }}
        >
          {isYouTube || isVimeo ? (
            <iframe
              src={getEmbedUrl(video.videoUrl)}
              title={video.title}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                border: 'none',
              }}
            />
          ) : (
            <video
              ref={videoRef}
              controls
              autoPlay
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
              }}
            >
              <source src={video.videoUrl} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          )}
        </Box>

        {/* Footer with tips */}
        <Box sx={{ p: 2, bgcolor: 'background.level1' }}>
          <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
            💡 Tip: You can access this tutorial anytime by clicking the help icon (?) on the page.
          </Typography>
        </Box>
      </ModalDialog>
    </Modal>
  );
};

export default VideoTutorial;
